import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { ROLE_COLORS, relTime } from "@/lib/orgUtils";
import { Search, Archive } from "lucide-react";

const SUBTABS = ["Audit", "Login", "Change Logs", "Error Logs"];

const ACTION_COLORS = {
  created: ["#00C875", "#00925a"], updated: ["#579BFC", "#2b6cb8"], moved: ["#A25DDC", "#A25DDC"],
  login: ["#181B34", "#676a82"], import: ["#FDAB3D", "#c97a10"], rollback: ["#E2445C", "#E2445C"],
  settings: ["#579BFC", "#2b6cb8"], error: ["#E2445C", "#E2445C"], deleted: ["#E2445C", "#E2445C"],
};

function Diff({ diff }) {
  const entries = Object.entries(diff || {});
  if (!entries.length) return <span className="text-[#9699AD]">—</span>;
  return (
    <div className="space-y-0.5">
      {entries.slice(0, 4).map(([k, v]) => (
        <p key={k} className="text-xs font-semibold">
          <span className="text-[#9699AD]">{k}:</span>{" "}
          <span className="line-through text-[#E2445C]">{String(v.old ?? "—")}</span>
          {" → "}
          <span className="text-[#00925a]">{String(v.new ?? "—")}</span>
        </p>
      ))}
    </div>
  );
}

function AuditTable({ lens }) {
  const [data, setData] = useState({ logs: [], total: 0 });
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(() => {
    api.get("/logs/audit", { params: { archived, q, event_type: type, lens } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [archived, q, type, lens]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap">
        {lens === "audit" && (
          <div className="flex gap-1">
            {[["Recent", false], ["Archived", true]].map(([l, v]) => (
              <button key={l} onClick={() => setArchived(v)} className={`px-3 h-8 rounded-full text-xs font-bold ${archived === v ? "bg-[#6161FF] text-white" : "bg-white border border-[#181B34]/10 text-[#4b4e63]"}`}>{l}</button>
            ))}
          </div>
        )}
        <div className="relative flex-1 min-w-52">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
          <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by record or user…"
            className="w-full h-9 pl-8 pr-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-semibold focus:outline-none" />
        </div>
        {lens === "audit" && (
          <>
            <select data-testid="audit-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="h-9 px-2 rounded-xl bg-white border border-[#181B34]/10 text-xs font-bold">
              <option value="">All actions</option>
              {Object.keys(ACTION_COLORS).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <button data-testid="archive-now-btn" onClick={() => api.post("/logs/audit/archive").then((r) => { toast.success(`${r.data.archived} entries archived`); load(); })}
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-[#181B34]/10 text-xs font-bold"><Archive size={13} /> Archive now</button>
          </>
        )}
        <span className="text-xs font-bold text-[#9699AD]">{data.logs.length} of {data.total} entries</span>
      </div>
      <div className="mt-4 bg-white rounded-2xl border border-[#181B34]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
              <th className="px-4 py-3">Time</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Record</th><th className="px-4 py-3">Changes</th><th className="px-4 py-3">Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.logs.map((l) => {
              const [bg, text] = ACTION_COLORS[l.event_type] || ACTION_COLORS.login;
              return (
                <tr key={l.id} className="border-b border-[#181B34]/5 align-top" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-2.5 text-xs font-semibold text-[#676a82] whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}<br /><span className="text-[#9699AD]">{relTime(l.timestamp)}</span></td>
                  <td className="px-4 py-2.5">
                    <p className="font-bold text-[13px]">{l.actor_name}</p>
                    <p className="text-[10px] font-semibold text-[#9699AD]">{ROLE_COLORS[l.actor_role]?.label || l.actor_role}</p>
                  </td>
                  <td className="px-4 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${bg}20`, color: text }}>{l.event_type}</span></td>
                  <td className="px-4 py-2.5 font-semibold text-[13px]">{l.record}</td>
                  <td className="px-4 py-2.5"><Diff diff={l.diff} /></td>
                  <td className="px-4 py-2.5 text-xs font-medium text-[#676a82]">{l.notes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.logs.length === 0 && <p className="p-6 text-center text-[#676a82] font-medium">No log entries.</p>}
      </div>
    </div>
  );
}

function LoginLog() {
  const [data, setData] = useState({ logs: [], kpis: {} });
  useEffect(() => { api.get("/logs/logins").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const K = [["Total Logins", data.kpis.total, "#6161FF"], ["Unique Users", data.kpis.unique, "#00C875"],
             ["Today", data.kpis.today, "#579BFC"], ["This Week", data.kpis.week, "#FDAB3D"], ["Failed Attempts", data.kpis.failed, "#E2445C"]];
  return (
    <div>
      <div className="flex gap-3 flex-wrap">
        {K.map(([l, v, c]) => (
          <div key={l} className="p-4 rounded-xl bg-white border border-[#181B34]/10 shadow-sm min-w-36" style={{ borderTop: `4px solid ${c}` }} data-testid={`login-kpi-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-3xl font-black">{v ?? "—"}</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mt-1">{l}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 bg-white rounded-2xl border border-[#181B34]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">IP</th><th className="px-4 py-3">Time</th><th className="px-4 py-3">Since</th>
            </tr>
          </thead>
          <tbody>
            {data.logs.map((l) => (
              <tr key={l.id} className="border-b border-[#181B34]/5" data-testid={`login-row-${l.id}`}>
                <td className="px-4 py-2.5 font-bold text-[13px]">{l.email}</td>
                <td className="px-4 py-2.5">{l.role && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: ROLE_COLORS[l.role]?.bg, color: ROLE_COLORS[l.role]?.text }}>{ROLE_COLORS[l.role]?.label}</span>}</td>
                <td className="px-4 py-2.5 text-xs font-bold uppercase text-[#676a82]">{l.method}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${l.status === "success" ? "bg-[#00C875]/15 text-[#00925a]" : "bg-[#E2445C]/12 text-[#E2445C]"}`}>{l.status}</span></td>
                <td className="px-4 py-2.5 text-xs font-mono text-[#676a82]">{l.ip || "—"}</td>
                <td className="px-4 py-2.5 text-xs font-semibold text-[#676a82]">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-xs font-bold text-[#9699AD]">{relTime(l.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.logs.length === 0 && <p className="p-6 text-center text-[#676a82] font-medium">No login events.</p>}
      </div>
    </div>
  );
}

export default function Logs() {
  const [sub, setSub] = useState(SUBTABS[0]);
  return (
    <div>
      <div className="flex gap-1.5 mb-6">
        {SUBTABS.map((s) => (
          <button key={s} data-testid={`logs-subtab-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setSub(s)}
            className={`px-4 h-9 rounded-full text-sm font-bold ${sub === s ? "bg-[#181B34] text-white" : "bg-white border border-[#181B34]/10 text-[#4b4e63]"}`}>{s}</button>
        ))}
      </div>
      {sub === "Audit" && <AuditTable lens="audit" />}
      {sub === "Login" && <LoginLog />}
      {sub === "Change Logs" && <AuditTable lens="changes" />}
      {sub === "Error Logs" && <AuditTable lens="errors" />}
    </div>
  );
}
